// Confirmação da consulta (VF-3) — pura.
//
// ★ CONFIRMAÇÃO É UM CAMPO, NÃO UM STATUS.
//
// Uma consulta confirmada continua AGENDADA: continua ocupando a vaga, continua
// esperando o paciente, continua podendo virar comparecimento ou falta.
// Confirmar é um ATRIBUTO do agendamento, não uma etapa dele.
//
// A alternativa — um quinto valor de `status` — quebraria cinco lugares que
// tratam `status === 'agendado'` como "ativo" (medidos em docs/vf3-plano.md).
// O pior deles é `consomeVaga`: uma consulta confirmada deixaria de ocupar a
// vaga, a grade reofereceria o horário, e dois pacientes cairiam no mesmo slot.
// Como campo, os cinco continuam corretos SEM SEREM TOCADOS.
//
// E é o que torna a peça inerte para o dinheiro: `status` nunca assume valor
// novo, e todo leitor de dinheiro compara `status` com 'compareceu' por
// igualdade estrita. Um campo que eles não leem não muda o resultado deles.

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Os cinco estados VISÍVEIS, com cor, glifo e rótulo.
 *
 * ★ COR NUNCA SOZINHA. Verde e azul — confirmado e compareceu — são justamente
 * o par que a deuteranopia mais confunde, e são os dois estados que mais
 * precisam ser distinguidos. Por isso cada um tem um glifo de FORMA diferente e
 * um rótulo em texto; a cor é o terceiro sinal, não o único.
 */
export const ESTADOS_VISUAIS = [
  { id: 'agendado', label: 'Agendado', cor: '#94a3b8', glifo: '○' },
  { id: 'confirmado', label: 'Confirmado', cor: '#16a34a', glifo: '✓' },
  { id: 'compareceu', label: 'Compareceu', cor: '#2563eb', glifo: '●' },
  { id: 'faltou', label: 'Faltou', cor: '#dc2626', glifo: '✕' },
  { id: 'cancelado', label: 'Cancelado', cor: '#64748b', glifo: '⊘' },
];

const DESCONHECIDO = { id: 'agendado', label: 'Agendado', cor: '#94a3b8', glifo: '○' };

export const estadoPorId = (id) =>
  ESTADOS_VISUAIS.find((e) => e.id === id) ?? DESCONHECIDO;

/** O agendamento tem carimbo de confirmação? */
export const temConfirmacao = (a) => paraData(a?.confirmadoEm) !== null;

/**
 * O estado que a tela mostra, derivado de (status, confirmadoEm).
 *
 * ★ A ORDEM É A REGRA: `compareceu` VENCE `confirmadoEm`.
 *
 * Quem confirmou e compareceu é AZUL, não verde. O carimbo da confirmação
 * continua gravado — é fato, e um dia dirá se confirmar serve para alguma coisa
 * — mas ele não pode esconder o fato mais novo. Só quem ainda está `agendado`
 * pode aparecer como confirmado, porque só nesse estado a confirmação ainda
 * descreve o presente.
 */
export function estadoVisual(agendamento) {
  const status = agendamento?.status ?? 'agendado';
  if (status === 'agendado') return temConfirmacao(agendamento) ? 'confirmado' : 'agendado';
  return ESTADOS_VISUAIS.some((e) => e.id === status) ? status : 'agendado';
}

/**
 * O flag só existe em consulta AGENDADA.
 *
 * Confirmar quem já faltou, cancelou ou compareceu não significa nada — e não
 * renderizar o alvo é o que impede o toque errado numa consulta resolvida.
 */
export const podeConfirmar = (agendamento) => (agendamento?.status ?? 'agendado') === 'agendado';

/**
 * O evento de auditoria de cada toque no flag.
 *
 * ⚠ SÓ IDs — nenhum nome de paciente, nenhum telefone. `auditoria` é lida pelo
 * gestor e é append-only (as rules negam update e delete), mas a regra do
 * projeto vale igual: trilha guarda contagem e identificador, nunca dado de
 * paciente. Quem precisa do nome tem o agendamento, que já o carrega.
 *
 * A trilha é UNIFORME ENTRE PAPÉIS: a recepção não escreve na timeline do lead
 * (as rules a reservam a `podeVendas`, e não vamos afrouxá-las), então ninguém
 * escreve. Duas histórias diferentes para o mesmo ato seriam pior que uma só.
 */
export function montarEventoConfirmacao(clinicaId, agendamento, marcar, autor) {
  return {
    tipo: 'agendamento.confirmacao',
    acao: marcar ? 'marcou' : 'desmarcou',
    agendamentoId: agendamento?.id ?? '',
    leadId: agendamento?.leadId ?? '',
    autorId: autor?.id ?? 'sistema',
    autorNome: autor?.nome ?? 'Sistema',
    autorPapel: autor?.papel ?? '',
    clinicaId,
  };
}
